import { useMemo } from "react";
import { TFunction, useTranslation } from "react-i18next";
import { ContractCategoryDto, useGetCategories } from "@rentablo/api/contracts";
import { SelectGroupedOptions } from "@rentablo/components";

export const groupCategories = (
  items: ContractCategoryDto[],
  t: TFunction,
  filterOutEmpty = true
) => {
  const categories = items.reduce((acc, item) => {
    if (item.type === "category") {
      acc.push({
        name: t(`contractAnalyzer.categories.${item.id}`),
        id: item.id,
        options: [],
      });
    } else if (item.type === "subCategory" && item.parentCategory?.id) {
      const parentGroup = acc.find(
        (group) => group.id === item.parentCategory?.id
      );
      const newSubCategory = {
        id: item.id,
        name: t(`contractAnalyzer.subCategories.${item.id}`),
      };

      if (parentGroup) {
        parentGroup.options.push(newSubCategory);
      } else {
        acc.push({
          id: item.parentCategory?.id,
          name: t(`contractAnalyzer.categories.${item.parentCategory.id}`),
          options: [newSubCategory],
        });
      }
    }
    return acc;
  }, [] as SelectGroupedOptions[]);

  const sorted = categories
    .map((category) => ({
      ...category,
      options: [...category.options].sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "")
      ),
    }))
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

  return filterOutEmpty
    ? sorted.filter((category) => Boolean(category.options.length))
    : sorted;
};

export const useGetContractCategories = () => {
  const { t } = useTranslation();
  const { data: categories, isFetching } = useGetCategories();

  const groupedCategories = useMemo(
    () => groupCategories(categories?.contractCategories ?? [], t),
    [categories, t]
  );

  return {
    data: groupedCategories,
    isFetching,
  };
};
